import { useMemo } from "react";
import type { Subscription } from "./SubscriptionsTable";

const REASON_META: Record<string, { label: string; emoji: string; color: string; bgColor: string; borderColor: string }> = {
  precio:             { label: "Precio alto",             emoji: "💰", color: "text-rose-700",   bgColor: "bg-rose-50",   borderColor: "border-rose-200" },
  "too-expensive":    { label: "Precio alto",             emoji: "💰", color: "text-rose-700",   bgColor: "bg-rose-50",   borderColor: "border-rose-200" },
  "sin-uso":          { label: "No lo usó",               emoji: "⚙️", color: "text-amber-700",  bgColor: "bg-amber-50",  borderColor: "border-amber-200" },
  "didnt-use":        { label: "No lo usó",               emoji: "⚙️", color: "text-amber-700",  bgColor: "bg-amber-50",  borderColor: "border-amber-200" },
  tecnico:            { label: "Problemas técnicos",      emoji: "🔧", color: "text-orange-700", bgColor: "bg-orange-50", borderColor: "border-orange-200" },
  technical:          { label: "Problemas técnicos",      emoji: "🔧", color: "text-orange-700", bgColor: "bg-orange-50", borderColor: "border-orange-200" },
  funcionalidades:    { label: "Faltan funcionalidades",  emoji: "🧩", color: "text-violet-700", bgColor: "bg-violet-50", borderColor: "border-violet-200" },
  "missing-features": { label: "Faltan funcionalidades",  emoji: "🧩", color: "text-violet-700", bgColor: "bg-violet-50", borderColor: "border-violet-200" },
  competidor:         { label: "Fue a la competencia",    emoji: "🔄", color: "text-sky-700",    bgColor: "bg-sky-50",    borderColor: "border-sky-200" },
  competitor:         { label: "Fue a la competencia",    emoji: "🔄", color: "text-sky-700",    bgColor: "bg-sky-50",    borderColor: "border-sky-200" },
  "sin-necesidad":    { label: "Ya no lo necesita",       emoji: "📵", color: "text-stone-700",  bgColor: "bg-stone-100", borderColor: "border-stone-200" },
  "no-need":          { label: "Ya no lo necesita",       emoji: "📵", color: "text-stone-700",  bgColor: "bg-stone-100", borderColor: "border-stone-200" },
};

const normalizeKey = (key: string): string => {
  if (key === "too-expensive") return "precio";
  if (key === "didnt-use") return "sin-uso";
  if (key === "technical") return "tecnico";
  if (key === "missing-features") return "funcionalidades";
  if (key === "competitor") return "competidor";
  if (key === "no-need") return "sin-necesidad";
  return key;
};

interface Props {
  subscriptions: Subscription[];
}

export default function ChurnPanel({ subscriptions }: Props) {
  const canceled = subscriptions.filter((s) => s.status === "canceled");
  const total = canceled.length;
  const withFeedback = canceled.filter((s) => !!s.cancellation_reason).length;
  const withoutFeedback = total - withFeedback;

  const reasonCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    canceled.forEach((s) => {
      if (!s.cancellation_reason) return;
      const key = normalizeKey(s.cancellation_reason);
      counts[key] = (counts[key] ?? 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([key, count]) => ({
        key,
        count,
        meta: REASON_META[key] ?? {
          label: key,
          emoji: "❓",
          color: "text-stone-700",
          bgColor: "bg-stone-50",
          borderColor: "border-stone-200",
        },
      }));
  }, [canceled]);

  const topReason = reasonCounts[0] ?? null;
  const maxCount = topReason?.count ?? 1;

  if (total === 0) {
    return (
      <div className="mb-4 rounded-2xl border border-stone-100 bg-white p-8 flex flex-col items-center gap-3 text-stone-400">
        <div className="w-12 h-12 flex items-center justify-center">
          <i className="ri-emotion-happy-line text-3xl text-stone-300" />
        </div>
        <p className="text-sm font-semibold text-stone-500">Sin cancelaciones registradas</p>
        <p className="text-xs text-stone-400 text-center max-w-xs">Cuando los clientes cancelen, aquí verás el análisis de motivos para identificar los principales problemas de churn.</p>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-2xl border border-stone-100 bg-white overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-stone-100 bg-stone-50 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-stone-200 flex items-center justify-center">
            <i className="ri-line-chart-line text-stone-600 text-base" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-stone-800">Análisis de churn</h3>
            <p className="text-xs text-stone-400">{total} cancelaciones · {withFeedback} con feedback ({withoutFeedback} sin)</p>
          </div>
        </div>

        {/* Coverage pill */}
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border ${
          withFeedback / Math.max(total, 1) > 0.6
            ? "bg-emerald-50 border-emerald-100"
            : "bg-amber-50 border-amber-100"
        }`}>
          <i className={`ri-bar-chart-2-line text-xs ${withFeedback / Math.max(total, 1) > 0.6 ? "text-emerald-500" : "text-amber-500"}`} />
          <span className={`text-xs font-bold ${withFeedback / Math.max(total, 1) > 0.6 ? "text-emerald-700" : "text-amber-700"}`}>
            {Math.round(withFeedback / Math.max(total, 1) * 100)}% con feedback
          </span>
        </div>
      </div>

      <div className="p-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main insight */}
          <div className="lg:col-span-1">
            {topReason ? (
              <div className={`rounded-2xl border p-5 h-full flex flex-col justify-between ${topReason.meta.bgColor} ${topReason.meta.borderColor}`}>
                <div>
                  <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Principal problema</p>
                  <div className="text-4xl mb-3">{topReason.meta.emoji}</div>
                  <p className={`text-lg font-black ${topReason.meta.color} leading-tight mb-1`}>
                    {topReason.meta.label}
                  </p>
                  <p className="text-xs text-stone-500 leading-relaxed">
                    {topReason.count} de {withFeedback} cancelaciones con feedback ({Math.round(topReason.count / Math.max(withFeedback, 1) * 100)}%)
                  </p>
                </div>
                <div className="mt-4 pt-4 border-t border-black/10">
                  <p className="text-xs text-stone-500 font-semibold">
                    {topReason.key === "precio"
                      ? "💡 Considera un plan de entrada o descuento de retención."
                      : topReason.key === "sin-uso"
                      ? "💡 Mejora el onboarding y el email de seguimiento día 3."
                      : topReason.key === "tecnico"
                      ? "💡 Revisa los tickets de soporte y el uptime de esta semana."
                      : topReason.key === "funcionalidades"
                      ? "💡 Comparte el roadmap — el cliente puede no saber qué viene."
                      : topReason.key === "competidor"
                      ? "💡 Solicita una llamada de salida para conocer qué ofrecen."
                      : "💡 Realiza entrevistas de salida para obtener más detalle."}
                  </p>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-stone-100 bg-stone-50 p-5 h-full flex flex-col items-center justify-center gap-2 text-stone-400">
                <i className="ri-bar-chart-line text-2xl" />
                <p className="text-xs text-center">Sin feedback todavía</p>
              </div>
            )}
          </div>

          {/* Bar chart */}
          <div className="lg:col-span-2">
            {reasonCounts.length > 0 ? (
              <div className="space-y-3">
                <p className="text-xs font-bold text-stone-500 uppercase tracking-widest">Desglose de motivos</p>
                {reasonCounts.map(({ key, count, meta }) => (
                  <div key={key} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{meta.emoji}</span>
                        <span className="text-xs font-semibold text-stone-700">{meta.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-stone-800">{count}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${meta.bgColor} ${meta.color} border ${meta.borderColor}`}>
                          {Math.round(count / Math.max(withFeedback, 1) * 100)}%
                        </span>
                      </div>
                    </div>
                    <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${
                          key === "precio" ? "bg-rose-400" :
                          key === "sin-uso" ? "bg-amber-400" :
                          key === "tecnico" ? "bg-orange-400" :
                          key === "funcionalidades" ? "bg-violet-400" :
                          key === "competidor" ? "bg-sky-400" :
                          "bg-stone-400"
                        }`}
                        style={{ width: `${Math.round(count / maxCount * 100)}%` }}
                      />
                    </div>
                  </div>
                ))}

                {withoutFeedback > 0 && (
                  <div className="space-y-1 opacity-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">❔</span>
                        <span className="text-xs font-semibold text-stone-500">Sin feedback</span>
                      </div>
                      <span className="text-xs font-bold text-stone-500">{withoutFeedback}</span>
                    </div>
                    <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-stone-300"
                        style={{ width: `${Math.round(withoutFeedback / maxCount * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center gap-3 text-stone-400 py-6">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-pie-chart-line text-2xl text-stone-300" />
                </div>
                <p className="text-xs text-center text-stone-400">
                  Aún no hay clientes que hayan enviado feedback de cancelación.
                  <br />
                  Aparecerá aquí automáticamente.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Churn rate footer */}
        <div className="mt-6 pt-4 border-t border-stone-100 grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-xl font-black text-stone-800">{total}</p>
            <p className="text-xs text-stone-400">Total canceladas</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-black text-emerald-600">{withFeedback}</p>
            <p className="text-xs text-stone-400">Con motivo</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-black text-amber-600">{withoutFeedback}</p>
            <p className="text-xs text-stone-400">Sin feedback</p>
          </div>
        </div>
      </div>
    </div>
  );
}
